import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, Smartphone, Zap, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DownloadCTAProps {
  articleId: string;
  articleTitle: string;
}

export const DownloadCTA = ({ articleId, articleTitle }: DownloadCTAProps) => {
  const { toast } = useToast();

  const trackConversion = async (conversionType: 'click' | 'download' | 'signup') => {
    try {
      const userAgent = navigator.userAgent;
      
      const { data: { user } } = await supabase.auth.getUser();

      await supabase.from('blog_conversions').insert({
        article_id: articleId,
        user_id: user?.id || null,
        conversion_type: conversionType,
        source: 'blog_post',
        user_agent: userAgent,
      });

      console.log(`Tracked ${conversionType} conversion for article:`, articleTitle);
    } catch (error) {
      console.error('Error tracking conversion:', error);
    }
  };

  const handleCTAClick = async () => {
    await trackConversion('click');
    
    // Check if user is already signed up
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      // Already a user, track as returning user
      toast({
        title: "Welcome back!",
        description: "Access your dashboard to start planning your next trip.",
      });
      window.location.href = '/dashboard';
    } else {
      // New user, track and redirect to signup
      await trackConversion('signup');
      window.location.href = '/auth';
    }
  };

  const handleAppStoreClick = async () => {
    await trackConversion('download');
    toast({
      title: "Coming Soon!",
      description: "OdyVoy mobile app will be available on iOS and Android soon.",
    });
  };

  return (
    <Card className="mt-12 p-8 bg-gradient-to-br from-primary/5 via-background to-secondary/5 border-2 border-primary/10">
      <div className="flex flex-col items-center text-center space-y-6">
        {/* Icon Grid */}
        <div className="flex gap-4 justify-center">
          <div className="p-3 bg-primary/10 rounded-full">
            <Smartphone className="h-6 w-6 text-primary" />
          </div>
          <div className="p-3 bg-primary/10 rounded-full">
            <Zap className="h-6 w-6 text-primary" />
          </div>
          <div className="p-3 bg-primary/10 rounded-full">
            <Globe className="h-6 w-6 text-primary" />
          </div>
        </div>

        {/* Heading */}
        <div className="space-y-2">
          <h3 className="text-2xl font-bold">Ready to Travel Smarter?</h3>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Get personalized packing lists, AI travel assistant, budget tracking, and more with OdyVoy
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-3xl text-sm">
          <div className="flex items-start gap-2">
            <div className="mt-0.5">✓</div>
            <div className="text-left">
              <strong>AI Packing Assistant</strong>
              <p className="text-muted-foreground">Never forget essentials again</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="mt-0.5">✓</div>
            <div className="text-left">
              <strong>Budget Tracking</strong>
              <p className="text-muted-foreground">Stay on top of expenses</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="mt-0.5">✓</div>
            <div className="text-left">
              <strong>Travel Insights</strong>
              <p className="text-muted-foreground">Weather, visa, health tips</p>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
          <Button 
            size="lg" 
            className="flex-1 text-base font-semibold"
            onClick={handleCTAClick}
          >
            <Download className="mr-2 h-5 w-5" />
            Get Started Free
          </Button>
          {/* Download App button hidden until native app is ready */}
        </div>

      </div>
    </Card>
  );
};
